import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../../services/api";
import PatientSearch, { type PatientResult } from "../../doctor/PatientSearch";
import PerioConsole from "../../doctor/perio/PerioConsole";
import ProcedureHistory from "../shared/ProcedureHistory";
import "../Treatments.css";

interface VisitRow {
  visit_id: number;
  visit_date?: string;
  visit_type?: string;
  status?: string;
  created_at?: string;
  [key: string]: unknown;
}

export default function PerioManager() {
    const [tab, setTab] = useState<'chart' | 'history'>('chart');
    const [patient, setPatient] = useState<{ patient_id: string | number; name?: string } | null>(null);
    const [visits, setVisits] = useState<VisitRow[]>([]);
    const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const norm = (raw: unknown) => String(raw ?? "").trim().toUpperCase().replace(/\s+/g, "_");
    const selectedVisit = useMemo(() => visits.find((v) => v.visit_id === selectedVisitId) || null, [visits, selectedVisitId]);
    const readOnly = Boolean(selectedVisit && norm(selectedVisit.status).includes("LOCK"));

    const loadVisits = useCallback(async (pid: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = (await apiGet(`/api/patients/${encodeURIComponent(pid)}/visits`)) as VisitRow[];
        const list = Array.isArray(data) ? data : [];
        const sorted = list.slice().sort((a, b) => String(b.visit_date || "").localeCompare(String(a.visit_date || "")) || (Number(b.visit_id) - Number(a.visit_id)));
        setVisits(sorted);
        setSelectedVisitId(sorted[0]?.visit_id ?? null);
      } catch (e: unknown) {
        setVisits([]);
        setSelectedVisitId(null);
        setError((e as Error)?.message || "Failed to load visits");
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      if (!patient?.patient_id) return;
      void loadVisits(String(patient.patient_id));
    }, [patient?.patient_id, loadVisits]);

    return (
        <div className="manager-wrapper">
            <div className="local-nav" style={{ marginBottom: '20px', borderBottom: '1px solid #ddd', display: 'flex', gap: '20px' }}>
                <button
                    style={{ padding: '10px', border: 'none', background: 'none', borderBottom: tab === 'chart' ? '2px solid #007bff' : 'none', cursor: 'pointer', fontWeight: tab === 'chart' ? 'bold' : 'normal' }}
                    onClick={() => setTab('chart')}
                >Perio Chart</button>
                <button
                    style={{ padding: '10px', border: 'none', background: 'none', borderBottom: tab === 'history' ? '2px solid #007bff' : 'none', cursor: 'pointer', fontWeight: tab === 'history' ? 'bold' : 'normal' }}
                    onClick={() => setTab('history')}
                >History</button>
            </div>

            {tab === 'chart' ? (
                <>
                    <h3>Periodontics</h3>
                    <p className="muted">Search patient → choose visit → chart. Locked visits are read-only.</p>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                      <div className="card" style={{ padding: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <div style={{ minWidth: 260 }}>
                            <div className="muted" style={{ marginBottom: 6 }}>Patient</div>
                            <PatientSearch
                              onSelect={(p: PatientResult) => setPatient({ patient_id: p.patient_id, name: p.name })}
                            />
                          </div>
                          <div style={{ minWidth: 240 }}>
                            <div className="muted" style={{ marginBottom: 6 }}>Visit</div>
                            <select
                              value={selectedVisitId ?? ""}
                              onChange={(e) => setSelectedVisitId(e.target.value ? Number(e.target.value) : null)}
                              disabled={!patient || loading || visits.length === 0}
                            >
                              <option value="">{!patient ? "Select patient first" : visits.length === 0 ? "No visits" : "Select visit"}</option>
                              {visits.map((v) => (
                                <option key={v.visit_id} value={v.visit_id}>
                                  #{v.visit_id} • {(v.visit_date || "").slice(0, 10) || "—"} • {v.visit_type || "Visit"} {norm(v.status).includes("LOCK") ? "(Locked)" : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                          {patient ? (
                            <button type="button" className="btn-glass" onClick={() => { setPatient(null); setVisits([]); setSelectedVisitId(null); }}>
                              Clear
                            </button>
                          ) : null}
                        </div>

                        {error ? <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>⚠️ {error}</div> : null}

                        {patient && selectedVisitId ? (
                          <div style={{ marginTop: 12 }}>
                            <PerioConsole visitId={selectedVisitId} readOnly={readOnly} />
                          </div>
                        ) : (
                          <div style={{ marginTop: 12 }} className="muted">
                            {patient ? "Select a visit to open perio chart." : "Select a patient to begin."}
                          </div>
                        )}
                      </div>
                    </div>
                </>
            ) : (
                <ProcedureHistory procedureType="Perio" title="Periodontal History" />
            )}
        </div>
    );
}
